import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Col,
  Row,
  Label,
} from "reactstrap";
import TextEditor from "components/editor/TextEditor";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams, useNavigate } from "react-router-dom"
import { httpRequest } from "utils/httpRequest";
import { toast } from 'react-toastify';

const EditPodcast = () => {
  const [responseVideoUrl, setResponseVideoUrl] = useState(null);
  const [videoLink, setVideoLink] = useState("")
  const [imageLink, setImageLink] = useState(null)

  const { id } = useParams()

  const navigate = useNavigate()

  // Validation Schema
  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Podcast title is required")
      .min(3, "Title must be at least 3 characters"),
    description: Yup.string()
      .required("Description is required")
      .test('not-empty-html', 'Description is required', function (value) {
        if (!value) return false;
        // Remove HTML tags and check if there's actual content
        const textOnly = value.replace(/<[^>]*>/g, '').trim();
        return textOnly.length > 0;
      }),

    video: Yup.mixed()
      .test("required", "A podcast file is required", function (value) {
        return value instanceof File || typeof value === 'string';
      })
      .test("fileType", "Only video files are allowed", function (value) {
        if (!value || typeof value === 'string') return true; // if it's a string URL or empty, don't validate type
        return ["video/mp4"].includes(value.type);
      }),

    image: Yup.mixed()
      .test('image-check', 'Image is required', function (value) {
        // Valid if we have either a file or an existing image path
        return value !== null && value !== undefined;
      })
      .test(
        "fileType",
        "Only image files are allowed",
        (value) => {
          // Skip validation if it's an existing image path (string)
          if (typeof value === 'string') return true;
          // Validate file type if it's a new upload
          return !value || ["image/jpeg", "image/png", "image/gif"].includes(value.type);
        }
      )
      .test(
        "fileSize",
        "Image must be smaller than 5MB",
        (value) => {
          // Skip validation if it's an existing image path
          if (typeof value === 'string') return true;
          return !value || value.size <= 5000000;
        }
      )
  });


  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      video: null,
      image: null,
      imagePreview: ""
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,


    context: { responseVideoUrl },
    onSubmit: async (values) => {
      formik.setTouched({
        title: true,
        description: true,
        video: true,
        image: true
      });

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) return false;

      try {

        let finalImage;

        if (values.image instanceof File) {
          finalImage = await getUploadedVideoLink(values.image);
        }
        // If it's a string (existing image path)
        else if (typeof values.image === 'string') {
          finalImage = values.image;
        }



        const videoToSend = values.video instanceof File ? videoLink : values.video;

        if (!videoToSend) {
          console.error("No video to submit.");
          return;
        }

        const response = await httpRequest(
          `podcast/update/${id}`,
          "PUT",
          {
            title: values.title,
            description: values.description,
            video: videoToSend,
            image: finalImage
          },
          {},
          true,
          true
        );
        if (response?.code === 200) {

          toast.success("Podcast updated successfully.")
          formik.resetForm()
          navigate("/admin/podcast-management")
        }
      } catch (error) {
        console.log(error);

      }
    },
  });

  const handleImageChange = async (e) => {
    const file = e.target.files[0];


    if (!file) return;
    const previewUrl = URL.createObjectURL(file);

    formik.setValues({
      ...formik.values,
      image: file,
      imagePreview: previewUrl
    });


    try {
      const uploadedImageLink = await getUploadedVideoLink(file);
      setImageLink(uploadedImageLink);
    } catch (error) {
      console.log("Upload failed:", error);
    }
  };


  // Memoize the video URLs to prevent recreation on every render
  const videoPreviewUrl = useMemo(() => {
    if (!formik.values.video) return null;

    if (typeof formik.values.video === 'string') {
      return `${process.env.REACT_APP_IMAGE_URL}${formik.values.video}`;
    }

    if (formik.values.video instanceof File) {
      return URL.createObjectURL(formik.values.video);
    }

    return null;
  }, [formik.values.video]);

  // Clean up the object URL when component unmounts or video changes
  useEffect(() => {
    return () => {
      if (videoPreviewUrl && formik.values.video instanceof File) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [videoPreviewUrl, formik.values.video]);

  const fetchPodcastEditData = async () => {
    try {
      const response = await httpRequest(
        `podcast/get/${id}`,
        "GET",
        {},
        {},
        true,
        false
      );
      if (response?.code === 200) {
        const videoUrl = response?.data?.video;

        setResponseVideoUrl(videoUrl);

        formik.resetForm({
          values: {
            title: response?.data?.title,
            description: response?.data?.description,
            video: videoUrl,
            image: response?.data?.image, // or null if you want fresh upload
            imagePreview: response?.data?.image
          }
        });
      }
    } catch (error) {
      console.log("Upload error:", error);
    }
  }

  useEffect(() => {
    if (id) {
      fetchPodcastEditData()
    }
  }, [id])

  const getUploadedVideoLink = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await httpRequest(
        "admin/UploadMedia",
        "POST",
        formData,
        {},
        true,
        true
      );
      return response?.filePath;
    } catch (error) {
      console.log("Upload error:", error);
      throw error;
    }
  };

  const handleVideoChange = async (event) => {
    const file = event.currentTarget.files[0]

    const allowedTypes = ["video/mp4"];

    if (!allowedTypes.includes(file?.type)) {
      toast.error("Unsupported video format. Only MP4 allowed.");
      formik.setFieldValue("video", null);
      return;
    }

    if (file) {
      formik.setFieldValue("video", file);
    }

    try {
      const uploadedVideoLink = await getUploadedVideoLink(file);
      setVideoLink(uploadedVideoLink);
    } catch (error) {
      console.log("Upload failed:", error);
    }
  }

  return (
    <Row className="mt-4 justify-content-center">
      <Col lg="8">
        <Card className="bg-secondary shadow border-0">
          <CardHeader className="bg-transparent pb-3">
            <div className="text-muted text-center mt-2 mb-3">
              <small>Edit Podcast</small>
            </div>
          </CardHeader>
          <CardBody className="px-lg-5 py-lg-4">
            <Form onSubmit={formik.handleSubmit}>
              {/* Podcast Title */}
              <FormGroup className="mb-3">
                <Label className="form-control-label">Podcast Title</Label>
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-book-bookmark" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Enter podcast title"
                    type="text"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </InputGroup>
                {formik.touched.title && formik.errors.title && (
                  <div className="text-danger small mt-1">
                    {formik.errors.title}
                  </div>
                )}
              </FormGroup>

              {/* Description */}
              <FormGroup>
                <Label className="form-control-label">Podcast Description</Label>
                <TextEditor
                  value={formik.values.description}
                  onChange={(html) => {
                    formik.setFieldValue("description", html)
                    setTimeout(() => {
                      formik.setFieldTouched("description", true);
                      formik.validateField("description");
                    }, 100);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched("description", true)
                    formik.validateField("description");
                  }}
                  placeholder="Write the podcast description..."
                  hideImageAndLink={false}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className="text-danger small mt-1">
                    {formik.errors.description}
                  </div>
                )}
              </FormGroup>

              {/* Video File */}
              {/* <FormGroup className="mb-4">
                <Label className="form-control-label">Podcast Video</Label> */}

              {/* Current video status and upload button */}
              {/* <div className="d-flex align-items-center gap-3 mb-3">
                  {formik.values.video ? (
                    <span className="text-success">
                      <i className="ni ni-check-bold mr-1" />
                      {typeof formik.values.video === 'string'
                        ? "Current video"
                        : formik.values.video.name || "New video selected"}
                    </span>
                  ) : (
                    <span className="text-muted">No video selected</span>
                  )}

                  <label className="btn btn-sm btn-primary mb-0 ml-2">
                    <i className="ni ni-cloud-upload-96 mr-1" />
                    {formik.values.video ? "Change Video" : "Upload Video"}
                    <input
                      type="file"
                      accept="video/mp4"
                      onChange={handleVideoChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div> */}

              {/* Video preview */}
              {/* {videoPreviewUrl && (
                  <div className="mt-2">
                    <video
                      controls
                      width="300"
                      height="180"
                      style={{
                        borderRadius: "6px",
                        objectFit: "cover",
                        display: "block"
                      }}
                      src={videoPreviewUrl}
                    />
                  </div>
                )}

                {formik.touched.video && formik.errors.video && (
                  <div className="text-danger small mt-1">
                    {formik.errors.video}
                  </div>
                )}
              </FormGroup>

              <FormGroup className="mb-4">
                <Label className="form-control-label" htmlFor="imageUpload">
                  Upload Article Cover Image
                </Label>
                <Input
                  type="file"
                  name="image"
                  id="imageUpload"
                  accept="image/*"
                  onChange={handleImageChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.image && formik.errors.image && (
                  <div className="text-danger small mt-1">
                    {formik.errors.image}
                  </div>
                )}
              </FormGroup>
              {formik.values.imagePreview && (
                <div className="mt-3">
                  <img
                    src={
                      typeof formik.values.imagePreview === 'string' &&
                        formik.values.imagePreview.startsWith('blob:')
                        ? formik.values.imagePreview // Use blob URL directly
                        : `${process.env.REACT_APP_IMAGE_URL}${formik.values.imagePreview}` // Prepend base URL for existing images
                    }
                    alt="Preview"
                    style={{ maxWidth: "25%", height: "auto", borderRadius: "0.25rem" }}
                    onError={(e) => {
                      e.target.src = '/path/to/fallback-image.jpg'; // Add fallback
                    }}
                  />
                </div>
              )} */}

              <div class="container text-center addflexwrap-video">
                <div class="row">
                  <div class="col-12 col-md-6">
                    <FormGroup className="mb-4 addflex-videoimage">
                      <Label className="form-control-label"> Video</Label>

                      {/* Upload button and status */}
                      <div className="d-flex align-items-center gap-3 mb-3 addmrginresponsive-video">
                        {formik.values.video ? (
                          <span className="text-success">
                            <i className="ni ni-check-bold" />
                            {formik.values.video.name || "Video selected"}
                          </span>
                        ) : (
                          ""
                        )}

                        <label className="btn btn-sm btn-primary mb-0 ml-2 addp-addvideo">
                          <i className="ni ni-cloud-upload-96 mr-1" />
                          {formik.values.video ? "Change Video" : "Upload Video"}
                          <input
                            type="file"
                            accept="video/mp4"
                            onChange={handleVideoChange}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>

                      {/* Video preview */}
                      {videoPreviewUrl && (
                        <div className="mt-2 w-100" >
                          <video
                            controls


                            style={{
                              width: "100%",
                              height: "180px",
                              borderRadius: "6px",
                              objectFit: "cover",
                              display: "block"
                            }}
                            src={videoPreviewUrl}
                          />
                        </div>
                      )}

                      {formik.touched.video && formik.errors.video && (
                        <div className="text-danger small mt-1">
                          {formik.errors.video}
                        </div>
                      )}
                    </FormGroup>
                  </div>
                  <div class="col-12 col-md-6">
                    <FormGroup className="mb-4 addflex-videoimage reducemargin-image">
                      <Label className="form-control-label" htmlFor="imageUpload">
                        Upload Cover Image
                      </Label>
                      <input
                        className="btn btn-sm btn-primary addw-imagebtn"
                        type="file"
                        name="image"
                        id="imageUpload"
                        accept="image/*"
                        onChange={handleImageChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.image && formik.errors.image && (
                        <div className="text-danger small mt-1">
                          {formik.errors.image}
                        </div>
                      )}
                    </FormGroup>

                    {/* Image Preview */}
                    {formik.values.imagePreview && (
                      <div className="mt-3 addmarginbtm-responsiveimage">
                        <img
                           src={
                            typeof formik.values.imagePreview === 'string' &&
                              formik.values.imagePreview.startsWith('blob:')
                              ? formik.values.imagePreview // Use blob URL directly
                              : `${process.env.REACT_APP_IMAGE_URL}${formik.values.imagePreview}` // Prepend base URL for existing images
                          }
                          alt="Preview"
                          style={{ width: "100%",
                            height: "180px",
                            borderRadius: "6px",
                            objectFit: "cover",
                            display: "block" }}
                          onError={(e) => {
                            e.target.src = '/path/to/fallback-image.jpg'; // Add fallback
                          }}
                        
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-center removemtop-edit">
                <Button className="my-4" color="primary" type="submit">
                  Save Podcast
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default EditPodcast;